import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import { useJarvisTheme } from '../theme/JarvisThemeProvider';
import {
  DEFAULT_UI_THEME,
  DEFAULT_UI_THEME_TOKENS,
  mixHex,
  normalizeTokens,
  softAccentFill,
  type JarvisUiThemeDefinition,
  type JarvisUiThemeTokens,
} from '../theme/tokens';
import { uiThemeService } from '../theme/uiTheme.service';

const emptyDraft = (): JarvisUiThemeTokens => ({ ...DEFAULT_UI_THEME_TOKENS });

function ColourField({
  label,
  value,
  onChange,
  helper,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  helper?: string;
  disabled?: boolean;
}) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Stack direction="row" gap={1} alignItems="center">
        {/* Native picker — Joy FormControl allows only one Input/Select/etc. */}
        <Box
          component="input"
          type="color"
          value={hex}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          aria-label={`${label} colour picker`}
          sx={{
            width: 40,
            height: 40,
            p: 0,
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
            borderRadius: 'sm',
            backgroundColor: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0B6BCB"
          disabled={disabled}
          sx={{ flex: 1 }}
        />
      </Stack>
      {helper && <FormHelperText>{helper}</FormHelperText>}
    </FormControl>
  );
}

function ThemePreview({ tokens }: { tokens: JarvisUiThemeTokens }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'sm',
        overflow: 'hidden',
        bgcolor: tokens.backgroundBody,
        display: 'flex',
        minHeight: 220,
      }}
    >
      <Box
        sx={{
          width: 108,
          flexShrink: 0,
          bgcolor: tokens.navBg,
          color: tokens.navText,
          borderRight: '1px solid',
          borderColor: tokens.navBorder,
          p: 1,
          fontFamily: tokens.fontFamily,
        }}
      >
        <Typography level="body-xs" fontWeight="lg" sx={{ color: 'inherit', mb: 1, fontFamily: 'inherit' }}>
          JARVIS
        </Typography>
        <Box
          sx={{
            px: 0.75,
            py: 0.4,
            borderRadius: 'sm',
            bgcolor: tokens.navSelectedBg,
            color: tokens.navSelectedText,
            fontSize: 11,
            mb: 0.4,
          }}
        >
          Selected
        </Box>
        <Box
          sx={{
            px: 0.75,
            py: 0.4,
            borderRadius: 'sm',
            bgcolor: tokens.navHoverBg,
            color: tokens.navHoverText,
            fontSize: 11,
            mb: 0.4,
          }}
        >
          Hover
        </Box>
        <Box sx={{ px: 0.75, py: 0.4, fontSize: 11, color: tokens.navMutedText }}>
          Idle
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ px: 1.5, py: 1, bgcolor: tokens.primary, color: '#fff' }}>
          <Typography level="body-sm" sx={{ color: 'inherit', fontFamily: tokens.fontFamily }}>
            Header / primary CTA
          </Typography>
        </Box>
        <Box sx={{ p: 1.5, bgcolor: tokens.backgroundLevel1, flex: 1 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 'sm',
              bgcolor: tokens.backgroundSurface,
              border: '1px solid',
              borderColor: 'divider',
              mb: 1,
            }}
          >
            <Typography level="title-sm" sx={{ color: tokens.textPrimary, fontFamily: tokens.fontFamily }}>
              Card surface
            </Typography>
            <Typography level="body-xs" sx={{ color: tokens.textSecondary, fontFamily: tokens.fontFamily, mb: 1 }}>
              Secondary text sample
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Box sx={{ px: 1.25, py: 0.5, borderRadius: 'sm', bgcolor: tokens.primary, color: '#fff', fontSize: 12 }}>
                Primary
              </Box>
              <Box sx={{ px: 1.25, py: 0.5, borderRadius: 'sm', bgcolor: tokens.accent, color: tokens.hoverText, fontSize: 12 }}>
                Accent
              </Box>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 'sm',
                  bgcolor: tokens.cardIconBg,
                  color: tokens.cardIconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                ◆
              </Box>
            </Stack>
          </Box>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 'sm',
              bgcolor: tokens.hoverBg,
              border: '1px solid',
              borderColor: tokens.hoverBorder,
              color: tokens.hoverText,
              mb: 1,
            }}
          >
            <Typography level="body-sm" fontWeight="lg" sx={{ color: 'inherit', fontFamily: tokens.fontFamily }}>
              Hover preview
            </Typography>
            <Typography level="body-xs" sx={{ color: 'inherit', opacity: 0.8, fontFamily: tokens.fontFamily }}>
              Stage tabs on hover
            </Typography>
          </Box>
          <Stack direction="row" gap={0.75}>
            {[
              { label: 'Idle', selected: false },
              { label: 'Selected', selected: true },
            ].map((tab) => (
              <Box
                key={tab.label}
                sx={{
                  flex: 1,
                  p: 0.75,
                  borderRadius: 'sm',
                  border: '1px solid',
                  borderColor: tab.selected ? tokens.stageSelectedBorder : tokens.stageBorder,
                  bgcolor: tab.selected ? tokens.stageSelectedBg : tokens.stageBg,
                  color: tab.selected ? tokens.stageSelectedText : tokens.stageText,
                  textAlign: 'center',
                  fontFamily: tokens.fontFamily,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    mx: 'auto',
                    mb: 0.35,
                    borderRadius: '50%',
                    bgcolor: tab.selected ? tokens.stageSelectedBadgeBg : tokens.stageBadgeBg,
                    color: tab.selected ? tokens.stageSelectedBadgeText : tokens.stageBadgeText,
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {tab.selected ? '2' : '1'}
                </Box>
                <Typography level="body-xs" sx={{ color: 'inherit', fontFamily: 'inherit', fontSize: 10 }}>
                  {tab.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default function UiThemeAdminPanel() {
  const { theme: activeTheme, themes, refresh, activateTheme, loading: themeLoading } = useJarvisTheme();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedKey, setSelectedKey] = useState(activeTheme.key);
  const [draftName, setDraftName] = useState('Keystone');
  const [draftTokens, setDraftTokens] = useState<JarvisUiThemeTokens>(emptyDraft);
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');

  useEffect(() => {
    setSelectedKey(activeTheme.key);
  }, [activeTheme.key]);

  const selected = useMemo(
    () => themes.find((t) => t.key === selectedKey) || DEFAULT_UI_THEME,
    [themes, selectedKey],
  );

  const selectTheme = (next: JarvisUiThemeDefinition) => {
    setSelectedKey(next.key);
    setMode('view');
    setError(null);
    if (!next.builtin) {
      setDraftName(next.name);
      setDraftTokens({ ...next.tokens });
    }
  };

  const startCreate = () => {
    setMode('create');
    setDraftName('Keystone');
    setDraftTokens({
      ...DEFAULT_UI_THEME_TOKENS,
      // Start from current live tokens so the new theme is a fork of today.
      ...activeTheme.tokens,
    });
    setError(null);
  };

  const startEdit = () => {
    if (selected.builtin) return;
    setMode('edit');
    setDraftName(selected.name);
    setDraftTokens({ ...selected.tokens });
    setError(null);
  };

  const saveCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await uiThemeService.createTheme(draftName, normalizeTokens(draftTokens));
      await refresh();
      setSelectedKey(created.key);
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create theme');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    setError(null);
    try {
      await uiThemeService.updateTheme(selected.key, {
        name: draftName,
        tokens: normalizeTokens(draftTokens),
      });
      await refresh();
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update theme');
    } finally {
      setBusy(false);
    }
  };

  const removeTheme = async () => {
    if (selected.builtin) return;
    const ok = window.confirm(`Delete theme “${selected.name}”?`);
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await uiThemeService.deleteTheme(selected.key);
      await refresh();
      setSelectedKey('default');
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete theme');
    } finally {
      setBusy(false);
    }
  };

  const activate = async (key: string) => {
    setBusy(true);
    setError(null);
    try {
      await activateTheme(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate theme');
    } finally {
      setBusy(false);
    }
  };

  const editingTokens = mode === 'view' ? selected.tokens : draftTokens;

  return (
    <Stack gap={2}>
      <Box>
        <Typography level="h4">Appearance</Typography>
        <Typography level="body-sm" sx={{ color: 'neutral.500', mt: 0.5 }}>
          App chrome colours and typography for JARVIS. Document PDF logos, headers, and footers stay under{' '}
          <strong>PDF Export</strong>.
        </Typography>
      </Box>

      {error && <Alert color="danger">{error}</Alert>}
      {!error && themeLoading && (
        <Alert color="neutral">Loading themes… If this never finishes, apply the UI themes migration.</Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="stretch">
        <Card variant="outlined" sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
              <Typography level="title-md">Themes</Typography>
              <Button size="sm" onClick={startCreate}>New theme</Button>
            </Stack>
            <Stack gap={0.75}>
              {themes.map((item) => {
                const active = item.key === activeTheme.key;
                const selectedRow = item.key === selected.key;
                return (
                  <Box
                    key={item.key}
                    onClick={() => selectTheme(item)}
                    sx={{
                      p: 1.1,
                      borderRadius: 'sm',
                      border: '1px solid',
                      borderColor: selectedRow ? 'primary.outlinedBorder' : 'divider',
                      bgcolor: selectedRow ? 'primary.softBg' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography level="body-sm" fontWeight="lg">{item.name}</Typography>
                      <Stack direction="row" gap={0.5}>
                        {item.builtin && <Chip size="sm" variant="soft">Built-in</Chip>}
                        {active && <Chip size="sm" color="success" variant="soft">Active</Chip>}
                      </Stack>
                    </Stack>
                    <Stack direction="row" gap={0.5} sx={{ mt: 0.75 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: item.tokens.primary, border: '1px solid', borderColor: 'divider' }} />
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: item.tokens.accent, border: '1px solid', borderColor: 'divider' }} />
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: item.tokens.navBg, border: '1px solid', borderColor: 'divider' }} />
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: item.tokens.navSelectedBg, border: '1px solid', borderColor: 'divider' }} />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography level="title-md">
                  {mode === 'create' ? 'Create theme' : selected.name}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  {mode === 'create'
                    ? 'Fork the current palette into a new named theme.'
                    : selected.builtin
                      ? 'Snapshot of the original JARVIS Joy defaults (read-only).'
                      : `Key: ${selected.key}`}
                </Typography>
              </Box>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {mode === 'view' && selected.key !== activeTheme.key && (
                  <Button size="sm" loading={busy} onClick={() => { void activate(selected.key); }}>
                    Activate
                  </Button>
                )}
                {mode === 'view' && !selected.builtin && (
                  <>
                    <Button size="sm" variant="outlined" onClick={startEdit}>Edit</Button>
                    <Button size="sm" variant="outlined" color="danger" loading={busy} onClick={() => { void removeTheme(); }}>
                      Delete
                    </Button>
                  </>
                )}
                {mode !== 'view' && (
                  <>
                    <Button size="sm" variant="plain" disabled={busy} onClick={() => setMode('view')}>Cancel</Button>
                    <Button
                      size="sm"
                      loading={busy}
                      onClick={() => { void (mode === 'create' ? saveCreate() : saveEdit()); }}
                    >
                      Save
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
              <Stack gap={1.25} sx={{ flex: 1 }}>
                {mode !== 'view' && (
                  <FormControl>
                    <FormLabel>Theme name</FormLabel>
                    <Input value={draftName} onChange={(e) => setDraftName(e.target.value)} />
                  </FormControl>
                )}
                <ColourField
                  label="Primary"
                  value={editingTokens.primary}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({
                    ...prev,
                    primary: v,
                    navSelectedBg: softAccentFill(v),
                    cardIconBg: softAccentFill(v),
                    chipSelectedBg: softAccentFill(v),
                    stageSelectedBg: softAccentFill(v),
                    stageSelectedBorder: mixHex(v, 0.45),
                    stageSelectedBadgeBg: mixHex(v, 0.78),
                  }))}
                  helper="Solid buttons, links, selected stage chrome"
                />
                <ColourField
                  label="Accent"
                  value={editingTokens.accent}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({
                    ...prev,
                    accent: v,
                    hoverBg: softAccentFill(v),
                    hoverBorder: v,
                    navHoverBg: softAccentFill(v),
                    navScrollbarThumbHover: v,
                    chipOutlineSelectedBorder: v,
                  }))}
                  helper="CTA hover, block hover wash, scrollbar highlight"
                />
                <ColourField
                  label="Hover background"
                  value={editingTokens.hoverBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, hoverBg: v }))}
                  helper="Stage tabs & lifecycle blocks on hover"
                />
                <ColourField
                  label="Hover border"
                  value={editingTokens.hoverBorder}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, hoverBorder: v }))}
                />
                <ColourField
                  label="Hover text"
                  value={editingTokens.hoverText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({
                    ...prev,
                    hoverText: v,
                    stageMetaHoverText: v,
                  }))}
                  helper="Text/icon colour on hover fill and accent button hover"
                />

                <Divider sx={{ my: 0.5 }} />
                <Typography level="title-sm">Navigation</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: -0.5 }}>
                  Main sidebar — independent from page Primary / Accent.
                </Typography>
                <ColourField
                  label="Nav background"
                  value={editingTokens.navBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navBg: v }))}
                />
                <ColourField
                  label="Nav text"
                  value={editingTokens.navText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navText: v }))}
                  helper="Idle item + icon colour"
                />
                <ColourField
                  label="Nav muted text"
                  value={editingTokens.navMutedText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navMutedText: v }))}
                />
                <ColourField
                  label="Nav border"
                  value={editingTokens.navBorder}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navBorder: v }))}
                  helper="Right edge and section dividers"
                />
                <ColourField
                  label="Nav selection block"
                  value={editingTokens.navSelectedBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navSelectedBg: v }))}
                />
                <ColourField
                  label="Nav selection text"
                  value={editingTokens.navSelectedText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navSelectedText: v }))}
                />
                <ColourField
                  label="Nav hover block"
                  value={editingTokens.navHoverBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navHoverBg: v }))}
                />
                <ColourField
                  label="Nav hover text"
                  value={editingTokens.navHoverText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navHoverText: v }))}
                />
                <ColourField
                  label="Nav scrollbar track"
                  value={editingTokens.navScrollbarTrack}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navScrollbarTrack: v }))}
                />
                <ColourField
                  label="Nav scrollbar thumb"
                  value={editingTokens.navScrollbarThumb}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navScrollbarThumb: v }))}
                />
                <ColourField
                  label="Nav scrollbar thumb hover"
                  value={editingTokens.navScrollbarThumbHover}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, navScrollbarThumbHover: v }))}
                />

                <Divider sx={{ my: 0.5 }} />
                <Typography level="title-sm">Stage tabs</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: -0.5 }}>
                  Workspace menu cards (Overview, Quotes / Estimates, Invoices, …) and lifecycle tanks.
                  Hover uses the Hover colours above.
                </Typography>
                <ColourField
                  label="Stage background"
                  value={editingTokens.stageBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageBg: v }))}
                />
                <ColourField
                  label="Stage text"
                  value={editingTokens.stageText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageText: v }))}
                />
                <ColourField
                  label="Stage border"
                  value={editingTokens.stageBorder}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageBorder: v }))}
                />
                <ColourField
                  label="Stage selected background"
                  value={editingTokens.stageSelectedBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageSelectedBg: v }))}
                />
                <ColourField
                  label="Stage selected text"
                  value={editingTokens.stageSelectedText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageSelectedText: v }))}
                />
                <ColourField
                  label="Stage selected border"
                  value={editingTokens.stageSelectedBorder}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageSelectedBorder: v }))}
                />
                <ColourField
                  label="Stage badge background"
                  value={editingTokens.stageBadgeBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageBadgeBg: v }))}
                />
                <ColourField
                  label="Stage badge text"
                  value={editingTokens.stageBadgeText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageBadgeText: v }))}
                />
                <ColourField
                  label="Stage selected badge background"
                  value={editingTokens.stageSelectedBadgeBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageSelectedBadgeBg: v }))}
                />
                <ColourField
                  label="Stage selected badge text"
                  value={editingTokens.stageSelectedBadgeText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageSelectedBadgeText: v }))}
                />
                <ColourField
                  label="Lifecycle progress text"
                  value={editingTokens.stageMetaText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageMetaText: v }))}
                  helper="Sub-numbers like 0 / 1 on idle lifecycle cards"
                />
                <ColourField
                  label="Lifecycle progress text (selected)"
                  value={editingTokens.stageSelectedMetaText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageSelectedMetaText: v }))}
                  helper="Use a light colour when the selected card is dark"
                />
                <ColourField
                  label="Lifecycle progress text (hover)"
                  value={editingTokens.stageMetaHoverText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, stageMetaHoverText: v }))}
                />

                <Divider sx={{ my: 0.5 }} />
                <Typography level="title-sm">Cards</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: -0.5 }}>
                  Icon chips inside quote / invoice / workspace cards.
                </Typography>
                <ColourField
                  label="Card icon background"
                  value={editingTokens.cardIconBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, cardIconBg: v }))}
                />
                <ColourField
                  label="Card icon colour"
                  value={editingTokens.cardIconColor}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, cardIconColor: v }))}
                  helper="Use white when the chip background is dark"
                />
                <ColourField
                  label="Filter chip background"
                  value={editingTokens.chipBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipBg: v }))}
                  helper="Solid chips under the workspace title"
                />
                <ColourField
                  label="Filter chip text"
                  value={editingTokens.chipText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipText: v }))}
                />
                <ColourField
                  label="Filter chip selected background"
                  value={editingTokens.chipSelectedBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipSelectedBg: v }))}
                />
                <ColourField
                  label="Filter chip selected text"
                  value={editingTokens.chipSelectedText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipSelectedText: v }))}
                />
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                  Outline chips (Products card) — border callout instead of solid fill.
                </Typography>
                <ColourField
                  label="Outline chip background"
                  value={editingTokens.chipOutlineBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipOutlineBg: v }))}
                />
                <ColourField
                  label="Outline chip text"
                  value={editingTokens.chipOutlineText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipOutlineText: v }))}
                />
                <ColourField
                  label="Outline chip selected background"
                  value={editingTokens.chipOutlineSelectedBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipOutlineSelectedBg: v }))}
                />
                <ColourField
                  label="Outline chip selected text"
                  value={editingTokens.chipOutlineSelectedText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipOutlineSelectedText: v }))}
                />
                <ColourField
                  label="Outline chip selected border"
                  value={editingTokens.chipOutlineSelectedBorder}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, chipOutlineSelectedBorder: v }))}
                />
                <ColourField
                  label="Selected item background"
                  value={editingTokens.selectionBg}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, selectionBg: v }))}
                  helper="Document rows, First/Final Survey tabs, supplier survey picker"
                />
                <ColourField
                  label="Selected item text"
                  value={editingTokens.selectionText}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, selectionText: v }))}
                  helper="Status chip keeps its own colours"
                />

                <Divider sx={{ my: 0.5 }} />
                <Typography level="title-sm">Page</Typography>
                <ColourField
                  label="Body background"
                  value={editingTokens.backgroundBody}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, backgroundBody: v }))}
                />
                <ColourField
                  label="Surface / cards"
                  value={editingTokens.backgroundSurface}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, backgroundSurface: v }))}
                />
                <ColourField
                  label="Level 1 background"
                  value={editingTokens.backgroundLevel1}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, backgroundLevel1: v }))}
                />
                <ColourField
                  label="Text primary"
                  value={editingTokens.textPrimary}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, textPrimary: v }))}
                />
                <ColourField
                  label="Text secondary"
                  value={editingTokens.textSecondary}
                  disabled={mode === 'view'}
                  onChange={(v) => setDraftTokens((prev) => ({ ...prev, textSecondary: v }))}
                />
                <FormControl>
                  <FormLabel>Font family</FormLabel>
                  <Input
                    value={editingTokens.fontFamily}
                    disabled={mode === 'view'}
                    onChange={(e) => setDraftTokens((prev) => ({ ...prev, fontFamily: e.target.value }))}
                  />
                  <FormHelperText>Currently Poppins across JARVIS.</FormHelperText>
                </FormControl>
              </Stack>

              <Box sx={{ width: { xs: '100%', lg: 300 }, flexShrink: 0 }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>Preview</Typography>
                <ThemePreview tokens={editingTokens} />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}
